/**
 * IMPORTANT! Do not use any services/composables here! (other than the ones defined in the context).
 *
 * This redirect handler should be used solely to determine whether user should be redirected
 * to a certain place or not, based on the application state, which is passed into the exported
 * function as first argument.
 *
 * Only imports that help to determine the redirect are allowed here (like consts or moment).
 */

import { FEATURE_FLAGS, BUSINESS_STATUSES } from '@/lib/consts.js'
import { useEppo } from '@/composables/eppo'

import { replaceWithResolvedRoute } from '../utils/replace-with-resolved-route'

import type { RedirectContext } from '../types'

// fallback (default route) - redirect to calendar or turn-tracker
export const defaultRouteRedirect = ({ store, router, logger }: RedirectContext): unknown => {
  const business = store.getters['business/business']
  const calendarRoute = { name: 'calendar', query: { date: 'today' } }
  const turnTrackerRoute = { name: 'turn-tracker' }
  const eppo = useEppo()
  const hasAccessToTurnTracker = store.getters['identity/isReceptionOrHigher']
  const turnTrackerEnabled = eppo.ff(FEATURE_FLAGS.Feature_TurnTracker)
    && business.turntracker_enabled

  const redirectToInvDetailsFlagEnabled = eppo.ff(FEATURE_FLAGS.Feature_ForceConfirmBillingInvoiceDataFlag)
  const invoiceDetailsRoute = { name: 'settings.subscription.billing-details.invoice-details' }
  const isConfirmed = store.state?.subscription?.invoiceDetails?.data?.is_confirmed
  const isOwner = store.getters['identity/isOwner']
  const correctBusinessStatus = [
    BUSINESS_STATUSES.PAID_ACTIVE,
    BUSINESS_STATUSES.ACTIVE_OVERDUE,
  ].includes(store.state?.business?.business?.status)

  let fallbackRoute: any = ''

  if (redirectToInvDetailsFlagEnabled) {
    if (isConfirmed === false && isOwner && correctBusinessStatus) {
      fallbackRoute = invoiceDetailsRoute
    } else if (turnTrackerEnabled && hasAccessToTurnTracker) {
      fallbackRoute = turnTrackerRoute
    } else {
      fallbackRoute = calendarRoute
    }
  } else {
    fallbackRoute = (turnTrackerEnabled && hasAccessToTurnTracker)
      ? turnTrackerRoute
      : calendarRoute
  }

  logger.debug(`fallback to default route >>> ${fallbackRoute.name}`)
  return replaceWithResolvedRoute(router, {
    ...fallbackRoute,
    params: {
      businessId: store.getters['business/businessId'],
    },
  })
}
